<script setup lang="ts">
import type { ScaleLinear } from 'd3';
import type { ScatterChartOptions } from '../index.vue';
import { useElementBounding } from '@vueuse/core';

// ================================================ Props
const bottomMargin = defineModel<number>({ required: true, default: 8 });
const props = defineProps<{
  loading: boolean;
  xScale: ScaleLinear<number, number>;
  options: ScatterChartOptions;
  containerWidth: number;
}>();

// ================================================ Data
const xAxisValues = ref<HTMLDivElement[]>([]);

// ================================================ Computed
const ticks = computed(() => {
  const domain = props.xScale.domain();
  const min = domain[0] || 0;
  const max = domain[1] || 0;
  if (min === max) return [0];

  const range = max - min;
  const numTicks = 8;
  const step = range / (numTicks - 1);

  return Array.from({ length: numTicks }, (_, i) => min + i * step);
});

// ================================================ Methods
const updateBottomMargin = (value: HTMLElement[]) => {
  if (!value.length) return 0;
  const values = value.map((value) => {
    const { height } = useElementBounding(value);
    return height.value;
  });
  bottomMargin.value = Math.max(...values) + 8;
};

const shouldShowLabel = (index: number) => {
  const totalLabels = ticks.value.length;
  if (totalLabels === 0) return false;

  // Estimer la largeur moyenne d'un label en pixels
  const avgLabelLength = 6; // Approximation pour les nombres
  const estimatedLabelWidth = avgLabelLength * 8;
  const minSpacing = 8;
  const requiredSpacePerLabel = estimatedLabelWidth + minSpacing;

  const maxVisibleLabels = Math.floor(props.containerWidth / requiredSpacePerLabel);

  if (maxVisibleLabels >= totalLabels) {
    return true;
  }

  const skipFactor = Math.max(1, Math.ceil(totalLabels / maxVisibleLabels));
  return index % skipFactor === 0;
};

const formatTick = (value: number) => {
  return Number(value.toFixed(1));
};

// ================================================ Watch
watch(
  () => xAxisValues.value,
  () => {
    updateBottomMargin(xAxisValues.value);
  },
  { deep: true, immediate: true },
);
</script>
<template>
  <div
    v-for="(tick, i) in ticks"
    ref="xAxisValues"
    :key="i"
    :style="{
      left: `${xScale(tick)}%`,
      top: '100%',
      transform: 'rotate(45deg) translateX(4px) translateY(8px)',
      display: shouldShowLabel(i) ? 'block' : 'none',
    }"
    class="absolute overflow-visible text-gray-400 mt-2"
  >
    <div ref="xAxisValues" class="absolute text-xs -translate-y-1/2 whitespace-pre">{{ formatTick(tick) }}</div>
  </div>
</template>
